import { App as AntApp, ConfigProvider, theme } from 'antd';
import ptBR from 'antd/locale/pt_BR';
import type { ReactNode } from 'react';
/*
 * As fontes entram por aqui porque este provider está na raiz dos dois apps:
 * é o único lugar de onde nenhuma tela escapa. Ver `tipografia.ts` para o
 * porquê de duas famílias.
 *
 * A sans é variável (um arquivo cobre 100-700); da serifa vêm só os dois pesos
 * que a interface usa, e só o recorte latino - ela aparece em preço e número,
 * não em parágrafo.
 */
import '@fontsource-variable/ibm-plex-sans/wght.css';
import '@fontsource/ibm-plex-serif/latin-300.css';
import '@fontsource/ibm-plex-serif/latin-400.css';
import './global.css';
import { ColorSchemeProvider, useColorScheme } from './color-scheme';
import type { ColorSchemePreference } from './color-scheme';
import { acentoToken, baseToken, brandColor, moduleToken, selectionToken, tintaBase } from './theme';
import type { ModuleKey } from './modules';

/**
 * Provider de tema. Fica na raiz de cada app.
 *
 * O Antd v6 usa CSS variables por padrão, então trocar de tema (claro/escuro ou
 * cor do módulo) não regera estilos — é troca de variável, instantânea.
 */

function ThemedRoot({ children }: { children: ReactNode }) {
  const { scheme } = useColorScheme();
  const escuro = scheme === 'dark';

  return (
    <ConfigProvider
      locale={ptBR}
      theme={{
        // O tom de seleção depende do esquema de cor, então é montado aqui e
        // não no token estático.
        token: { ...baseToken, ...selectionToken(brandColor, escuro), ...tintaBase(escuro) },
        algorithm: escuro ? theme.darkAlgorithm : theme.defaultAlgorithm,
      }}
    >
      <AntApp>{children}</AntApp>
    </ConfigProvider>
  );
}

export function TitularisProvider({
  children,
  padraoDeTema,
}: {
  children: ReactNode;
  /**
   * O tema antes de a pessoa escolher. Omitido, segue o sistema - que é o certo
   * para o app e para o admin. A landing passa `light`, porque o desenho dela
   * foi decidido em claro e quem chega de um link não pediu o escuro.
   */
  padraoDeTema?: ColorSchemePreference;
}) {
  return (
    <ColorSchemeProvider padrao={padraoDeTema}>
      <ThemedRoot>{children}</ThemedRoot>
    </ColorSchemeProvider>
  );
}

/**
 * Aplica a cor do módulo em uma subárvore, sem tocar no resto do app.
 * É o que dá a cada módulo a sensação de produto próprio.
 */
export function ModuleThemeProvider({
  module,
  children,
}: {
  module: ModuleKey;
  children: ReactNode;
}) {
  const { scheme } = useColorScheme();

  return (
    <ConfigProvider theme={{ token: moduleToken(module, scheme === 'dark') }}>
      {children}
    </ConfigProvider>
  );
}

/**
 * Mesmo tema dos módulos, para área que não é fase de prova (D-20).
 *
 * A Legislação Comentada usa isto: ela precisa parecer parte da plataforma, com
 * o mesmo espaçamento e a mesma lógica de cor dos demais módulos, sem virar um
 * valor de `ModuleKey` - que é a fase da prova, e não o produto.
 */
export function AcentoThemeProvider({ acento, children }: { acento: string; children: ReactNode }) {
  const { scheme } = useColorScheme();

  return (
    <ConfigProvider theme={{ token: acentoToken(acento, scheme === 'dark') }}>{children}</ConfigProvider>
  );
}
